import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ContentBlockType } from '@onesource/schemas';
import { useCompleteBlock } from '@onesource/services';
import { createFileRoute } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { useEffect, useRef } from 'react';

import { useResolvedBlock } from '#/features/community-hub/components/block-viewers/use-resolved-block';
import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';

/**
 * Embed item viewer. Path: /programs/{program}/{module}/{lesson}/embed/{itemSlug}
 * Shows an embed content block (URL iframe or sanitized HTML embed code)
 */
export const Route = createFileRoute('/_protected/programs/$programSlug/$moduleSlug/$lessonSlug/embed/$itemSlug')({
  component: EmbedContentRoute,
});

const sanitizeEmbedHtml = (raw: string) =>
  DOMPurify.sanitize(raw, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'width', 'height', 'sandbox', 'referrerpolicy'],
  });

function EmbedContentRoute() {
  const { itemSlug } = Route.useParams();
  const {
    programId,
    moduleId,
    lessonId,
    programSlug,
    moduleSlug,
    lessonSlug,
    lesson,
    module,
    findParentLesson,
    block,
    isLoading,
    error,
  } = useResolvedBlock(itemSlug, ContentBlockType.EMBED);

  // Embed is a view block: opening it completes it.
  const trackedRef = useRef(false);
  const { mutate: completeBlock } = useCompleteBlock();
  useEffect(() => {
    if (!trackedRef.current && block) {
      trackedRef.current = true;
      completeBlock(block.id);
    }
  }, [block, completeBlock]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {error.message}</Alert>
      </Box>
    );
  }

  if (!block) {
    return (
      <Box p={3}>
        <Alert severity="error">Content not found</Alert>
      </Box>
    );
  }

  const raw = block.body?.trim() ?? '';
  const isUrl = /^https?:\/\//i.test(raw);

  return (
    <Box>
      <LessonBreadcrumbs
        spaceId={programId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Embed"
        programSlug={programSlug}
        moduleSlug={moduleSlug}
        lessonSlug={lessonSlug}
      />

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          {block.title || 'Embed'}
        </Typography>
      </Box>

      <Box pb={4}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            maxWidth: 1100,
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {raw ? (
            <Box
              sx={{
                width: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'grey.100',
                '& iframe': {
                  display: 'block',
                  width: '100%',
                  minHeight: { xs: 320, md: 650 },
                  border: 0,
                },
              }}
            >
              {isUrl ? (
                <Box
                  component="iframe"
                  src={raw}
                  title={block.title || 'Embedded content'}
                  sx={{ width: '100%', height: { xs: 320, md: 650 }, border: 0 }}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  allowFullScreen
                />
              ) : (
                <Box dangerouslySetInnerHTML={{ __html: sanitizeEmbedHtml(raw) }} />
              )}
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No embed content available.
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
